<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { computed, nextTick, onBeforeMount, ref, watch } from 'vue'
import dayjs from 'dayjs'
import Draggable from 'vuedraggable'
import axios from 'axios'
import { v4 as uuid4 } from 'uuid'
import { SortableEvent } from 'sortablejs'
import { notify } from '@kyvg/vue3-notification'

import { localeDate, localeTime, differenceFromMinutes } from '@/utils/dates'
import { image } from '@/utils/assets'
import { numberFormatter } from '@/utils/numbers'
import useForm from '@/hooks/use-form'
import {
    type AdrInformation,
    EventType,
    FetchShipmentsFilter,
    MinimalShipment,
    ShipmentType,
    TemperatureRequirement,
    Tour,
    TourEvent,
} from '@/types/delivery-management'
import { TransportObjectType } from '@/types/transport-object'
import { groupTourEvents } from '@/utils/delivery-management/group-tour-events'
import { ResourceResponse, uuid } from '@/types/general'
import { useAuthStore } from '@/stores/auth-store'
import { splitAddressString } from '@/utils/string'
import { DragChangeEvent } from '@/types/sortable'
import { destinationAddress, shipmentTypes } from '@/utils/delivery-management'
import { DropdownOption } from '@/types/inputs'

import MyPanel from '@/components/my-components/MyPanel.vue'
import ShipmentPicker from '@/components/delivery-management/manage-tour/ShipmentPicker.vue'
import MyLabeledField from '@/components/my-components/MyLabeledField.vue'
import GroupedTourEventsCard from '@/components/delivery-management/tour-planner/GroupedTourEventsCard.vue'
import MyButton from '@/components/my-components/MyButton.vue'
import DraggableShipmentCard from '@/components/delivery-management/tour-planner/DraggableShipmentCard.vue'
import MySelect from '@/components/my-components/form/MySelect.vue'
import MyRadioButton from '@/components/my-components/form/MyRadioButton.vue'
import MyRadioButtonGroup from '@/components/my-components/form/MyRadioButtonGroup.vue'
import TourModal from '@/components/delivery-management/tour-modal/TourModal.vue'
import ManageTourModal from '@/components/delivery-management/manage-tour/ManageTourModal.vue'
import ManageShipmentModal from '@/components/delivery-management/ManageShipmentModal.vue'
import MyDateRangePicker from '@/components/my-components/MyDateRangePicker.vue'
import ShipmentTemperatureIcon from '@/components/delivery-management/ShipmentTemperatureIcon.vue'

interface AdrInfo {
    adrInfo: AdrInformation
    count: number
}

type LimitedTour = Pick<
    Tour,
    | 'id'
    | 'name'
    | 'plannedAt'
    | 'startedAt'
    | 'endedAt'
    | 'dispatcherUser'
    | 'hub'
    | 'haulierCompany'
    | 'destinationHub'
    | 'optimizationDetails'
>

interface PlanningTour extends LimitedTour {
    eventGroups: EventGroup[]
    shipmentsCount: number
    stopsCount: number
    vehicles: string
    units: string
    driverNames: string
    totalVolume: string
    refrigeratedCount: number
    frozenCount: number
    adrInformation: AdrInfo[]
}

interface EventGroup {
    id: string
    pickups: number
    deliveries: number
    address: string
    zipcodeCity: string
    tourEvents: TourEvent[]
}

interface LinkedShipment {
    id: uuid
    name: string
    tourEventId: uuid
}

interface Form {
    tours: Tour[]
}

type DragElementType = EventGroup | MinimalShipment | TourEvent

const { t } = useI18n()
const authStore = useAuthStore()

const { data, submit } = useForm<Form>({ tours: [] })
const tourDateFilter = ref<{
    'start-date': string | undefined
    'end-date': string | undefined
}>({
    'start-date': dayjs().startOf('day').toISOString(),
    'end-date': dayjs().endOf('day').toISOString(),
})
const shipmentFilters = ref<FetchShipmentsFilter>({
    'for-tour-or-without': undefined,
    type: undefined,
})
const selectedTourId = ref<string>()
const tourModalOpen = ref(false)
const manageTourModalOpen = ref(false)
const shipmentModalOpen = ref(false)
const shipmentPickerRef = ref<InstanceType<typeof ShipmentPicker>>()
// Track tours with unsaved changes. Used to disable ETA/Optimize buttons until saved.
const modifiedTourIds = ref<Set<string>>(new Set())
const calculatingEta = ref<Record<string, boolean>>({})
const optimizingTour = ref<Record<string, boolean>>({})

// We need a dummy computed prop for this since the filter can be undefined but MySelect doesn't handle that
const shipmentFiltersType = computed({
    get: () => shipmentFilters.value.type || null,
    set(value) {
        shipmentFilters.value.type = value || undefined
    },
})

const shipmentTypeOptions = computed<DropdownOption[]>(() => {
    return Object.entries(shipmentTypes).map(([value, translation]) => ({
        label: t(translation),
        value,
    }))
})

const preparedTours = computed(() => {
    return data.tours.map<PlanningTour>((tour) => {
        const result = groupTourEvents(tour.events)

        // Pre-calculate registration numbers
        const vehicles =
            tour.transportObjects
                .filter((o) => o.type === TransportObjectType.Vehicle)
                .map((o) => o.registrationNumber ?? '')
                .filter((r) => r)
                .join(', ') || '-'

        const units =
            tour.transportObjects
                .filter((o) => o.type === TransportObjectType.Unit)
                .map((o) => o.registrationNumber ?? '')
                .filter((r) => r)
                .join(', ') || '-'

        // Pre-calculate driver names
        const driverNames =
            tour.drivers.length > 0 ? tour.drivers.map((d) => d.user.name).join(', ') : '-'

        // Pre-calculate volume
        const totalVolumeNum = tour.events
            .filter((event) => event.shipment)
            .reduce((sum, event) => sum + (event.shipment!.totalVolume ?? 0), 0)
        const totalVolume = totalVolumeNum === 0 ? '—' : numberFormatter.format(totalVolumeNum)

        // Pre-calculate temperature counts
        const refrigeratedCount = tour.events.filter(
            (event) =>
                event.shipment?.temperatureRequirement === TemperatureRequirement.Refrigerated,
        ).length

        const frozenCount = tour.events.filter(
            (event) => event.shipment?.temperatureRequirement === TemperatureRequirement.Frozen,
        ).length

        // Pre-calculate ADR information
        const adrMap = new Map<string, { adrInfo: AdrInformation; shipmentIds: Set<string> }>()
        for (const event of tour.events) {
            if (!event.shipment?.collis) continue
            for (const colli of event.shipment.collis) {
                if (colli.adrInformation) {
                    const unNumber = colli.adrInformation.unNumber
                    if (!adrMap.has(unNumber)) {
                        adrMap.set(unNumber, {
                            adrInfo: colli.adrInformation,
                            shipmentIds: new Set(),
                        })
                    }
                    adrMap.get(unNumber)!.shipmentIds.add(event.shipment.id)
                }
            }
        }
        const adrInformation = Array.from(adrMap.values()).map((item) => ({
            adrInfo: item.adrInfo,
            count: item.shipmentIds.size,
        }))

        // Build event groups
        const eventGroups = result.groups.map((events) => {
            const firstShipment = events[0]?.shipment
            const address = firstShipment
                ? splitAddressString(destinationAddress(firstShipment))
                : { address: '', zipcode: null, city: null }

            return {
                id: events.map((e) => e.id).join('-'),
                pickups: events.filter((e) => e.shipment?.type === ShipmentType.Pickup).length,
                deliveries: events.filter((e) => e.shipment?.type === ShipmentType.Delivery).length,
                address: address.address || '-',
                zipcodeCity: `${address.zipcode || ''} ${address.city || ''}`.trim() || '-',
                tourEvents: events,
            }
        })

        return {
            id: tour.id,
            name: tour.name,
            plannedAt: tour.plannedAt,
            startedAt: tour.startedAt,
            endedAt: tour.endedAt,
            dispatcherUser: tour.dispatcherUser,
            haulierCompany: tour.haulierCompany,
            hub: tour.hub,
            destinationHub: tour.destinationHub,
            optimizationDetails: tour.optimizationDetails,
            eventGroups,
            shipmentsCount: tour.events.filter((event) => event.shipment).length,
            stopsCount: eventGroups.length,
            vehicles,
            units,
            driverNames,
            totalVolume,
            refrigeratedCount,
            frozenCount,
            adrInformation,
        }
    })
})

function formatDuration(seconds: number | null | undefined): string {
    if (!seconds) return '-'
    return differenceFromMinutes(Math.floor(seconds / 60))
}

function formatDistance(meters: number | null | undefined): string {
    if (!meters) return '-'
    const km = meters / 1000
    return km.toFixed(1)
}

function formatAdrString(adr: { unNumber: string; name: string; class: string }): string {
    return `${adr.unNumber}: ${adr.name} class ${adr.class}`
}

const linkedShipments = computed(() => {
    const shipments: Record<uuid, LinkedShipment> = {}
    for (const tour of data.tours) {
        for (const tourEvent of tour.events) {
            if (!tourEvent.shipment) continue
            shipments[tourEvent.shipment.id] = {
                id: tour.id,
                name: tour.name,
                tourEventId: tourEvent.id,
            }
        }
    }

    return shipments
})

function setTourDateFilter(date: string | undefined, key: 'start-date' | 'end-date') {
    tourDateFilter.value[key] = date
}

function getInitials(name: string): string {
    const names = name.split(' ')
    if (names.length === 0) return ''

    return (names[0][0] + names[names.length - 1][0]).toUpperCase()
}

function showTour(tourId: string) {
    selectedTourId.value = tourId
    tourModalOpen.value = true
}

async function onDragChange(tour: PlanningTour, event: DragChangeEvent<DragElementType>) {
    const tourIndex = data.tours.findIndex((t) => t.id === tour.id)
    if (tourIndex === -1) return

    if (event.moved) {
        // `tourEventGroups` is already reordered by Draggable so we just need to retrieve it from the planned tour
        const newGroups = tour.eventGroups

        data.tours[tourIndex] = {
            ...data.tours[tourIndex]!,
            events: newGroups.flatMap((g) => g.tourEvents),
        }
    } else if (event.added) {
        // In case a shipment that already exists on the tour is dragged from the ShipmentsPicker again we'll just abort
        const existingTourLink = linkedShipments.value[event.added.element.id]
        if (existingTourLink?.id === tour.id) {
            return
        }

        // In case a shipment is dragged in from ShipmentsPicker that is already on another tour we'll remove it from that tour first
        if (existingTourLink && isShipment(event.added.element)) {
            const oldTour = preparedTours.value.find((t) => t.id === existingTourLink.id)
            const oldTourEvent = data.tours
                .flatMap((t) => t.events)
                .find((e) => e.id === existingTourLink.tourEventId)
            if (oldTour && oldTourEvent) {
                onDragChange(oldTour, { removed: { element: oldTourEvent, oldIndex: -1 } })
            }
        }

        // We'll wait for next tick so that we can ensure the event is removed first when we drag a tour event out of a grouped stop
        await nextTick()
        const newEventList = data.tours[tourIndex].events
        const newGroupIndex = event.added.newIndex
        let leftGroup = tour.eventGroups[newGroupIndex - 1]
        // In case there a no tour events in the group we can assume that it's the groups last shipment that is moved just to the right of its group
        // Therefore we'll use the group just before to that
        if (leftGroup?.tourEvents.length === 0) {
            leftGroup = tour.eventGroups[newGroupIndex - 2]
        }
        // Determine insert position: after the last event of the left group, or at start
        const insertAfterId = leftGroup?.tourEvents.at(-1)?.id
        const insertIndex = insertAfterId
            ? newEventList.findIndex((te) => te.id === insertAfterId) + 1
            : 0

        let tourEvents: TourEvent[] = []
        if (isEventGroup(event.added.element)) {
            tourEvents = event.added.element.tourEvents
        } else if (isShipment(event.added.element)) {
            // We'll create a dummy tour event for now
            tourEvents = [
                {
                    id: uuid4(),
                    tourId: tour.id,
                    shipment: event.added.element,
                    waypoint: null,
                    driver: null,
                    type: EventType.Shipment,
                    order: data.tours[tourIndex].events.length,
                    deliveryLocation: null,
                    transactions: [],
                    plannedDuration: 0,
                    estimatedAt: null,
                    estimatedDepartureAt: null,
                    liveEstimatedAt: null,
                    driverEstimatedAt: null,
                    createdAt: '',
                    updatedAt: '',
                    handledAt: null,
                },
            ]
        } else {
            tourEvents = [event.added.element]
        }
        newEventList.splice(insertIndex, 0, ...tourEvents)
    } else if (event.removed) {
        let removedTourEventIds: uuid[] = []
        if (isEventGroup(event.removed.element)) {
            removedTourEventIds = event.removed.element.tourEvents.map((te) => te.id)
        } else if (isTourEvent(event.removed.element)) {
            removedTourEventIds = [event.removed.element.id]
        }

        data.tours[tourIndex] = {
            ...data.tours[tourIndex]!,
            events: data.tours[tourIndex].events.filter((te) => {
                return !removedTourEventIds.includes(te.id)
            }),
        }
    }

    // Mark tour as modified so Calculate ETA and Optimize Tour buttons are disabled
    modifiedTourIds.value.add(tour.id)
}

function onShipmentMoved(event: SortableEvent): boolean {
    // We'll only allow moving to our tours
    return event.from !== event.to
}

function isEventGroup(element: DragElementType): element is EventGroup {
    return 'tourEvents' in element
}

function isShipment(element: DragElementType): element is MinimalShipment {
    return 'sendingLocation' in element
}

function isTourEvent(element: DragElementType): element is TourEvent {
    return 'estimatedAt' in element
}

async function fetchTours() {
    const params = {
        company: authStore.companyId,
        'start-date': tourDateFilter.value['start-date'],
        'end-date': tourDateFilter.value['end-date'],
    }
    const response = await axios.get<ResourceResponse<Tour[]>>(
        window.route('dm.company.tours.for-tour-planner', params),
    )

    data.tours = response.data.data
}

async function saveTours() {
    const response = await submit(
        'PUT',
        window.route('dm.company.tours.update-multiple', { company: authStore.companyId }),
    )

    if (response) {
        notify({ type: 'success', text: t('toursUpdated') })
        modifiedTourIds.value.clear()
        await fetchTours()
    }
}

function isTourModified(tourId: string): boolean {
    return modifiedTourIds.value.has(tourId)
}

async function calculateEta(tour: PlanningTour) {
    calculatingEta.value[tour.id] = true
    try {
        await axios.put(
            window.route('dm.company.tours.calculate-eta', {
                tour: tour.id,
                company: authStore.companyId,
            }),
        )
        notify({ type: 'success', text: t('calculateEtaSuccess') })
        await fetchTours()
    } catch (error) {
        notify({ type: 'error', text: t('failedEtaCalculation') })
    } finally {
        calculatingEta.value[tour.id] = false
    }
}

async function optimizeTour(tour: PlanningTour) {
    optimizingTour.value[tour.id] = true
    try {
        await axios.put(
            window.route('dm.company.tours.optimize', {
                tour: tour.id,
                company: authStore.companyId,
            }),
        )
        notify({ type: 'success', text: t('optimizeSuccess') })
        await fetchTours()
    } catch (error) {
        notify({ type: 'error', text: t('failedOptimization') })
    } finally {
        optimizingTour.value[tour.id] = false
    }
}

async function onShipmentSaved() {
    await fetchTours()
    shipmentPickerRef.value?.fetchAvailableShipments()
}

onBeforeMount(fetchTours)
watch(tourDateFilter, fetchTours, { deep: true })
</script>

<template>
    <div class="flex space-x-4 w-full min-h-screen">
        <MyPanel
            panel-class="w-full overflow-visible !max-w-[500px] flex-shrink-0"
            class="flex flex-col pb-0"
            padded
        >
            <MyButton
                v-if="!authStore.isReadonly.deliveryManagement"
                plain
                scheme="primary"
                size="small"
                @click="shipmentModalOpen = true"
            >
                <mdi:plus-thick class="mr-1" />
                {{ t('addShipment') }}
            </MyButton>

            <ShipmentPicker
                ref="shipmentPickerRef"
                class="h-full"
                :boxed="false"
                :filter="shipmentFilters"
                with-collis
            >
                <template #filters>
                    <div class="mt-2 flex flex-col space-y-2">
                        <MySelect
                            v-model="shipmentFiltersType"
                            :label="t('shipmentType')"
                            :options="shipmentTypeOptions"
                            filter-name="type"
                        />

                        <MyRadioButtonGroup
                            v-model="shipmentFilters['for-tour-or-without']"
                            :label="t('linkedToTours')"
                        >
                            <MyRadioButton :value="undefined" :label="t('all')" />
                            <MyRadioButton value="none" :label="t('unlinked')" />
                        </MyRadioButtonGroup>

                        <!-- temperature requirement -->
                        <!-- un numbers -->
                        <!-- zip code range -->
                    </div>
                </template>

                <template #shipments="{ shipments }">
                    <Draggable
                        :model-value="shipments"
                        item-key="id"
                        handle=".handle"
                        ghost-class="drag-ghost-card"
                        :group="{ name: 'event-groups', put: true, pull: 'clone' }"
                        :move="onShipmentMoved"
                        :animation="200"
                        class="flex flex-col h-full space-y-3"
                    >
                        <template #item="{ element: shipment }">
                            <DraggableShipmentCard
                                :shipment="shipment"
                                :linked-tour="linkedShipments[shipment.id]"
                            />
                        </template>
                    </Draggable>
                </template>
            </ShipmentPicker>
        </MyPanel>

        <!-- Tour list -->
        <!-- min-w-0 is to avoid the container expanding out of its parent size -->
        <div class="w-full flex flex-col space-y-4 min-w-0">
            <div class="flex justify-between items-center space-x-4">
                <MyDateRangePicker
                    :filter="tourDateFilter"
                    @start-date-changed="setTourDateFilter($event, 'start-date')"
                    @end-date-changed="setTourDateFilter($event, 'end-date')"
                />

                <div class="flex space-x-4">
                    <MyButton
                        plain
                        scheme="primary"
                        size="small"
                        @click="manageTourModalOpen = true"
                    >
                        <mdi:plus-thick class="mr-1" />
                        {{ t('addTour') }}
                    </MyButton>

                    <MyButton scheme="primary" size="small" @click="saveTours">
                        <mdi:content-save class="mr-1" />
                        {{ t('saveChanges') }}
                    </MyButton>
                </div>
            </div>

            <div
                v-if="preparedTours.length === 0"
                class="flex h-full items-center justify-center dark:text-primary-300 py-12"
            >
                {{ t('noAvailableTours') }}
            </div>

            <MyPanel v-for="tour in preparedTours" :key="tour.id" disable-loader>
                <!-- Tour information -->
                <div class="flex space-x-4 py-2 px-4">
                    <div class="flex flex-col">
                        <div class="flex items-center text-lg font-bold text-primary-400">
                            <div
                                v-if="tour.dispatcherUser"
                                v-tooltip="tour.dispatcherUser.name"
                                class="h-[30px] w-[30px] flex aspect-square items-center justify-center rounded-full bg-primary-300 mr-2"
                            >
                                <img
                                    v-if="tour.dispatcherUser?.profileImage"
                                    :src="image(tour.dispatcherUser.profileImage)"
                                    :alt="tour.dispatcherUser.name"
                                    class="object-cover h-full rounded-full"
                                />
                                <span
                                    v-else
                                    class="text-primary-100 text-sm"
                                    v-text="getInitials(tour.dispatcherUser?.name ?? '')"
                                />
                            </div>

                            {{ tour.name }}
                        </div>
                        <span
                            class="text-stone-700 dark:text-white text-sm"
                            v-text="localeDate(tour.plannedAt)"
                        />
                    </div>

                    <div class="flex flex-col">
                        <MyLabeledField :title="t('truck')" :content="tour.vehicles" />
                        <MyLabeledField :title="t('trailers')" :content="tour.units" />
                    </div>

                    <div class="flex flex-col">
                        <MyLabeledField :title="t('drivers')" :content="tour.driverNames" />
                        <MyLabeledField
                            :title="t('haulier')"
                            :content="tour.haulierCompany?.name ?? '-'"
                        />
                    </div>

                    <div class="flex flex-col">
                        <MyLabeledField
                            :title="t('startLocation')"
                            :content="tour.hub?.name ?? '-'"
                        />
                        <MyLabeledField
                            :title="t('hub')"
                            :content="tour.destinationHub?.name ?? '-'"
                        />
                    </div>

                    <div class="flex flex-col">
                        <MyLabeledField
                            :title="t('startTime')"
                            :content="tour.startedAt ? localeTime(tour.startedAt) : '-'"
                        />
                        <MyLabeledField
                            v-if="tour.optimizationDetails"
                            :title="t('expectedEnd')"
                            :content="tour.endedAt ? localeTime(tour.endedAt) : '-'"
                        />
                    </div>

                    <div class="flex flex-col">
                        <MyLabeledField
                            :title="t('drivingTime')"
                            :content="formatDuration(tour.optimizationDetails?.duration)"
                        />
                        <MyLabeledField
                            :title="t('km')"
                            :content="
                                formatDistance(tour.optimizationDetails?.distance) ??
                                t('notSpecified')
                            "
                        />
                    </div>

                    <div class="flex flex-col">
                        <MyLabeledField
                            :title="t('shipments')"
                            :content="tour.shipmentsCount.toString()"
                        />
                        <MyLabeledField :title="t('stops')" :content="tour.stopsCount.toString()" />
                    </div>

                    <div class="flex flex-col">
                        <MyLabeledField
                            :title="t('totalCbm')"
                            :content="tour.totalVolume + ' m³'"
                        />
                        <MyLabeledField :title="t('totalLdm')" content="-" />
                    </div>

                    <div
                        v-if="
                            tour.refrigeratedCount > 0 ||
                            tour.frozenCount > 0 ||
                            tour.adrInformation.length > 0
                        "
                        class="flex flex-col gap-2"
                    >
                        <div
                            v-if="tour.refrigeratedCount > 0 || tour.frozenCount > 0"
                            class="flex items-center gap-4"
                        >
                            <span v-if="tour.refrigeratedCount > 0" class="flex items-center gap-1">
                                <span class="font-semibold leading-none">
                                    {{ tour.refrigeratedCount }}
                                </span>
                                <ShipmentTemperatureIcon
                                    class="relative -top-px"
                                    :requirement="TemperatureRequirement.Refrigerated"
                                    icon-only
                                />
                            </span>

                            <span v-if="tour.frozenCount > 0" class="flex items-center gap-1">
                                <span class="font-semibold leading-none">
                                    {{ tour.frozenCount }}
                                </span>
                                <ShipmentTemperatureIcon
                                    class="relative -top-px"
                                    :requirement="TemperatureRequirement.Frozen"
                                    icon-only
                                />
                            </span>
                        </div>

                        <div v-if="tour.adrInformation.length > 0" class="flex items-center gap-4">
                            <span
                                v-for="adrItem in tour.adrInformation"
                                :key="adrItem.adrInfo.unNumber"
                                class="flex items-center gap-1"
                            >
                                <span class="font-semibold">{{ adrItem.count }}</span>
                                <img
                                    v-for="labelImage in adrItem.adrInfo.labelImages"
                                    :key="labelImage"
                                    v-tooltip="formatAdrString(adrItem.adrInfo)"
                                    class="w-[30px] h-[30px]"
                                    :src="image(labelImage)"
                                    :alt="adrItem.adrInfo.class"
                                />
                            </span>
                        </div>
                    </div>

                    <div class="ml-auto flex items-center space-x-2">
                        <MyButton
                            v-tooltip="t('calculateTourETAExplanation')"
                            scheme="primary"
                            size="small"
                            :loading="calculatingEta[tour.id]"
                            :disabled="isTourModified(tour.id)"
                            @click="calculateEta(tour)"
                        >
                            {{ t('calculateTourETA') }}
                            <mdi:lightning-bolt />
                        </MyButton>

                        <MyButton
                            v-tooltip="t('optimizeTourExplanation')"
                            scheme="primary"
                            size="small"
                            :loading="optimizingTour[tour.id]"
                            :disabled="isTourModified(tour.id)"
                            @click="optimizeTour(tour)"
                        >
                            {{ t('optimizeTour') }}
                            <mdi:lightning-bolt />
                        </MyButton>

                        <MyButton
                            v-tooltip="t('viewTour')"
                            plain
                            scheme="primary"
                            size="small"
                            icon
                            @click="showTour(tour.id)"
                        >
                            <mdi:eye />
                        </MyButton>
                    </div>
                </div>

                <Draggable
                    :list="tour.eventGroups"
                    item-key="id"
                    class="bg-stone-300 dark:bg-dark-400 w-full flex items-start p-2 space-x-4 overflow-x-scroll scroll-container"
                    handle=".handle"
                    ghost-class="drag-ghost-card"
                    group="event-groups"
                    :animation="200"
                    @change="onDragChange(tour, $event)"
                >
                    <template #item="{ element: group, index }">
                        <GroupedTourEventsCard
                            :group="group"
                            :index="index"
                            @drag-change="onDragChange(tour, $event)"
                        />
                    </template>
                </Draggable>
            </MyPanel>
        </div>

        <TourModal v-if="selectedTourId" :id="selectedTourId" v-model:open="tourModalOpen" />

        <ManageTourModal
            v-if="manageTourModalOpen"
            v-model="manageTourModalOpen"
            @close="manageTourModalOpen = false"
            @saved="fetchTours()"
        />

        <ManageShipmentModal
            v-model="shipmentModalOpen"
            @close="shipmentModalOpen = false"
            @saved="onShipmentSaved"
        />
    </div>
</template>

<style>
@reference "@css/app.css";
/* Stacked card effect */
.grouped-event::before {
    position: absolute;
    content: '';
    left: 0;
    bottom: -5px;
    margin: 0 5px;
    width: calc(100% - 0.375rem * 2);
    height: 10px;
    @apply rounded-b-xl bg-primary-50/40 dark:bg-dark-500/60;
}
</style>
